import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtError, fmtDateTime, API } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, DownloadSimple, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * /detailed-approvals — pending detailed-report queue across all campaigns.
 * View: execution, execution_head, super_admin.
 * Decide (approve/reject): execution_head + super_admin only.
 */
export default function DetailedApprovals() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [decisionFor, setDecisionFor] = useState(null); // {report, decision}
    const canDecide = ["execution_head", "super_admin"].includes(user?.role);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/detailed-reports/pending-approvals");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="p-6 md:p-10 max-w-6xl" data-testid="detailed-approvals-page">
            <div className="mb-6">
                <div className="overline">Approvals</div>
                <h1 className="font-display text-3xl">Detailed report approvals</h1>
                <p className="text-sm text-slate-500 mt-2">
                    Pending detailed reports across all campaigns.
                    {!canDecide && " View-only for your role — Execution Head signs off."}
                </p>
            </div>

            {loading && <p className="text-xs font-mono text-slate-400">loading…</p>}

            {!loading && items.length === 0 && (
                <div className="border border-dashed border-slate-300 p-16 text-center" data-testid="detailed-approvals-empty">
                    <p className="text-sm text-slate-500">Nothing pending. All detailed reports are up to date.</p>
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="text-left px-3 py-2 overline">Campaign</th>
                                <th className="text-left px-3 py-2 overline">Filename</th>
                                <th className="text-left px-3 py-2 overline">Rows</th>
                                <th className="text-left px-3 py-2 overline">Uploaded by</th>
                                <th className="text-left px-3 py-2 overline">Uploaded at</th>
                                <th className="text-right px-3 py-2 overline">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`detailed-approvals-row-${r.id}`}>
                                    <td className="px-3 py-2">
                                        <Link
                                            to={`/campaigns/${r.campaign_id}`}
                                            className="inline-flex items-center gap-1 font-medium text-slate-900 hover:underline"
                                            data-testid={`detailed-approvals-campaign-${r.id}`}
                                        >
                                            {r.campaign_name || "Untitled"} <ArrowUpRight size={12} weight="bold" />
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs truncate max-w-[240px]">{r.filename}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.row_count}</td>
                                    <td className="px-3 py-2 text-xs">{r.created_by_name}</td>
                                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{fmtDateTime(r.created_at)}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <a
                                            href={`${API}/detailed-reports/${r.id}/csv`}
                                            className="p-1.5 inline-flex text-slate-500 hover:text-slate-900"
                                            title="Download CSV"
                                            data-testid={`detailed-approvals-csv-${r.id}`}
                                        >
                                            <DownloadSimple size={14} />
                                        </a>
                                        {canDecide && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => setDecisionFor({ report: r, decision: "approved" })}
                                                    className="p-1.5 text-slate-500 hover:text-green-700"
                                                    title="Approve"
                                                    data-testid={`detailed-approvals-approve-${r.id}`}
                                                >
                                                    <CheckCircle size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDecisionFor({ report: r, decision: "rejected" })}
                                                    className="p-1.5 text-slate-500 hover:text-red-700"
                                                    title="Reject"
                                                    data-testid={`detailed-approvals-reject-${r.id}`}
                                                >
                                                    <XCircle size={14} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {decisionFor && (
                <DecisionModal
                    report={decisionFor.report}
                    decision={decisionFor.decision}
                    onClose={() => setDecisionFor(null)}
                    onDecided={() => { setDecisionFor(null); load(); }}
                />
            )}
        </div>
    );
}


function DecisionModal({ report, decision, onClose, onDecided }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (decision === "rejected" && !reason.trim()) {
            toast.error("Rejection reason is required.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/detailed-reports/${report.id}/decision`, {
                decision,
                reason: reason.trim() || null,
            });
            toast.success(`Report ${decision}.`);
            onDecided?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="detailed-approvals-decision-modal">
            <div className="bg-white border border-slate-200 max-w-md w-full mt-16">
                <div className="px-6 py-4 border-b border-slate-200">
                    <div className="overline">Approval</div>
                    <h3 className="font-display text-lg">{decision === "approved" ? "Approve" : "Reject"} detailed report</h3>
                </div>
                <div className="p-6 space-y-3">
                    <p className="text-sm text-slate-700 font-mono">
                        {report.campaign_name} · {report.filename} · {report.row_count} rows
                    </p>
                    {decision === "rejected" && (
                        <div>
                            <label className="overline block mb-1.5">Rejection reason</label>
                            <Textarea
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                data-testid="detailed-approvals-decision-reason"
                                className="rounded-none border-slate-300"
                                placeholder="Why is this being rejected?"
                            />
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        data-testid="detailed-approvals-decision-submit"
                        className={`rounded-none text-white px-6 ${decision === "approved" ? "bg-green-700 hover:bg-green-800" : "bg-red-700 hover:bg-red-800"}`}
                    >
                        {busy ? "…" : (decision === "approved" ? "Approve" : "Reject")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
